import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { lazy, Suspense, useEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { monoInk } from "../../lib/mono";
import { FiGithub, FiLinkedin, FiMail } from "react-icons/fi";

const MidnightBloom = lazy(() => import("../ui/midnight-bloom-gradient"));
const WisteriaDaylight = lazy(() => import("../ui/wisteria-daylight-gradient"));

/** Base stop of each hero field, shown until that theme's gradient loads. */
const HERO_PLATE_DARK = "#12142E";
const HERO_PLATE_LIGHT = "#F7F4F8";
/** Drift speed for the hero field, 0–100. The baked recipe runs at 36. */
const HERO_BLOOM_SPEED = 12;

const ease = [0.22, 1, 0.36, 1] as const;
const GRID_CELL = 80;
const SLOGAN_WORDS = ["hero.slogan.w1", "hero.slogan.w2", "hero.slogan.w3"] as const;
const SLOGAN_MS = 4500;

const heroSocials = [
  { icon: FiGithub, label: "GitHub", href: "https://github.com/Charcrith" },
  { icon: FiLinkedin, label: "LinkedIn", href: "https://linkedin.com/in/charcrith" },
  { icon: FiMail, label: "Email", href: "mailto:charcrith@gmail.com" },
];

function Slogan({
  ink,
  muted,
  reduceMotion,
}: {
  ink: string;
  muted: string;
  reduceMotion: boolean;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % SLOGAN_WORDS.length);
    }, SLOGAN_MS);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const word = t(SLOGAN_WORDS[reduceMotion ? 0 : index]!);

  return (
    <h2
      className="text-[clamp(2.15rem,6.5vw,4.25rem)] font-semibold leading-[1.02] tracking-[-0.03em]"
      style={{ color: ink }}
    >
      <span className="block" style={{ color: ink }}>
        {t("hero.slogan.lead")}
      </span>
      <span className="mt-2 flex items-baseline gap-[0.28em] pl-[0.55em]">
        <span style={{ color: muted }}>{t("hero.slogan.to")}</span>
        {reduceMotion ? (
          <span style={{ color: ink }}>{word}</span>
        ) : (
          <AnimatePresence mode="wait">
            <motion.span
              key={word}
              className="inline-block"
              style={{ color: ink }}
              initial={{ opacity: 0, y: "0.35em" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "-0.35em" }}
              transition={{ duration: 0.55, ease }}
            >
              {word}
            </motion.span>
          </AnimatePresence>
        )}
      </span>
    </h2>
  );
}

function HeroGrid({
  ink,
  sectionRef,
}: {
  ink: string;
  sectionRef: RefObject<HTMLElement | null>;
}) {
  const colRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!fine) return;

    const bands = [colRef.current, rowRef.current, cellRef.current];
    const place = (x: number, y: number) => {
      const col = Math.floor(x / GRID_CELL) * GRID_CELL;
      const row = Math.floor(y / GRID_CELL) * GRID_CELL;
      if (colRef.current) colRef.current.style.transform = `translate3d(${col}px,0,0)`;
      if (rowRef.current) rowRef.current.style.transform = `translate3d(0,${row}px,0)`;
      if (cellRef.current) cellRef.current.style.transform = `translate3d(${col}px,${row}px,0)`;
      for (const band of bands) {
        if (band) band.style.opacity = "1";
      }
    };
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      place(e.clientX - r.left, e.clientY - r.top);
    };
    const onLeave = () => {
      for (const band of bands) {
        if (band) band.style.opacity = "0";
      }
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, [sectionRef]);

  const hairline = `color-mix(in srgb, ${ink} 16%, transparent)`;
  const band = `color-mix(in srgb, ${ink} 3%, transparent)`;
  const cell = `color-mix(in srgb, ${ink} 5%, transparent)`;

  return (
    <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden>
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(to right, ${hairline} 1px, transparent 1px), linear-gradient(to bottom, ${hairline} 1px, transparent 1px)`,
          backgroundSize: `${GRID_CELL}px ${GRID_CELL}px`,
        }}
      />
      <div
        ref={colRef}
        className="absolute top-0 bottom-0 hidden opacity-0 transition-opacity duration-150 md:block"
        style={{ width: GRID_CELL, background: band }}
      />
      <div
        ref={rowRef}
        className="absolute left-0 right-0 hidden opacity-0 transition-opacity duration-150 md:block"
        style={{ height: GRID_CELL, background: band }}
      />
      <div
        ref={cellRef}
        className="absolute top-0 left-0 hidden opacity-0 transition-opacity duration-150 md:block"
        style={{
          width: GRID_CELL,
          height: GRID_CELL,
          background: cell,
          boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${ink} 14%, transparent)`,
        }}
      />
    </div>
  );
}

function BlueprintHud({
  ink,
  muted,
  sectionRef,
}: {
  ink: string;
  muted: string;
  sectionRef: RefObject<HTMLElement | null>;
}) {
  const labelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    const label = labelRef.current;
    if (!el || !label) return;

    const show = (text: string, color: string, opacity: string) => {
      label.textContent = text;
      label.style.color = color;
      label.style.opacity = opacity;
    };
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const x = Math.round(e.clientX - r.left);
      const y = Math.round(e.clientY - r.top);
      show(`x:${String(x).padStart(4, "0")} y:${String(y).padStart(4, "0")}`, ink, "0.85");
    };
    const onLeave = () => show("x:---- y:----", muted, "0.5");

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, [sectionRef, ink, muted]);

  return (
    <div className="pointer-events-none absolute inset-0 z-[2] hidden md:block">
      <div
        className="absolute right-[calc(1.25rem+var(--site-frame))] bottom-[calc(1rem+var(--site-frame))] tabular-nums select-none"
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          color: muted,
          fontSize: 10,
          letterSpacing: "0.1em",
        }}
      >
        <span ref={labelRef} style={{ color: muted, opacity: 0.5 }}>
          x:---- y:----
        </span>
      </div>
    </div>
  );
}

export const Hero = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const plate = isDark ? HERO_PLATE_DARK : HERO_PLATE_LIGHT;
  const Field = isDark ? MidnightBloom : WisteriaDaylight;
  const { ink, muted } = monoInk(isDark);
  const reduceMotion = useReducedMotion() ?? false;
  const sectionRef = useRef<HTMLElement>(null);

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-[100svh] flex-col overflow-x-clip lg:overflow-hidden"
      style={{
        backgroundColor: plate,
        color: ink,
        paddingTop: "max(5.25rem, calc(env(safe-area-inset-top) + 4rem))",
        paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))",
      }}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <Suspense fallback={null}>
          <Field
            speed={HERO_BLOOM_SPEED}
            paused={reduceMotion}
            style={{ width: "100%", height: "100%", aspectRatio: "auto" }}
          />
        </Suspense>
      </div>
      <HeroGrid ink={ink} sectionRef={sectionRef} />
      <BlueprintHud ink={ink} muted={muted} sectionRef={sectionRef} />

      <div className="pointer-events-auto absolute top-1/2 right-[max(1.85rem,calc(var(--site-frame)+1.65rem))] z-20 flex -translate-y-1/2 flex-col gap-7">
        {heroSocials.map(({ icon: Icon, label, href }) => (
          <a
            key={label}
            href={href}
            target={href.startsWith("mailto:") ? undefined : "_blank"}
            rel={href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
            aria-label={label}
            className="transition-opacity hover:opacity-100"
            style={{ color: ink, opacity: 0.72 }}
          >
            <Icon className="size-[18px]" aria-hidden />
          </a>
        ))}
      </div>

      <div className="pointer-events-auto absolute bottom-[max(2.75rem,calc(var(--site-frame)+2.25rem))] left-[max(1.75rem,calc(var(--site-frame)+1.75rem))] z-20 max-w-[min(36rem,calc(100%-7rem))] sm:bottom-[max(3.5rem,calc(var(--site-frame)+3rem))] sm:left-[max(2.75rem,calc(var(--site-frame)+2.75rem))] lg:left-[max(4.5rem,calc(var(--site-frame)+4rem))]">
        <Slogan ink={ink} muted={muted} reduceMotion={reduceMotion} />
      </div>
    </section>
  );
};
